import type React from "react";
import { useContext, useState } from "react";
import { Lines } from "../../../assets";
import type { UserAccount } from "../BankingInterface";
import { formatUniversalNumber } from "../../../lib/utils";
import { Check, RefreshCw } from "lucide-react";
import { LinkedAccountsContext } from "../../../providers/LinkedAccountsProvider";
import { Button } from "../../../components/ui/button";

interface PaymentCardProps {
  account: UserAccount & { lastSynced?: string | Date };
  cardType?: "credit" | "debit" | "transfer";
  isSelected?: boolean;
  onSelect?: (accountId: string) => void;
  onSync?: (accountId: string) => Promise<void>;
}

// Define type for styles object
type StylesType = {
  credit: {
    background: string;
    textPrimary: string;
    cardTitle: string;
    accentColor: string;
  };
  debit: {
    background: string;
    textPrimary: string;
    cardTitle: string;
    accentColor: string;
  };
  transfer: {
    background: string;
    textPrimary: string;
    cardTitle: string;
    accentColor: string;
  };
  default: {
    background: string;
    textPrimary: string;
    cardTitle: string;
    accentColor: string;
  };
};

// Helper function to format last synced time
const getLastSyncedTime = (lastSynced?: string | Date | null) => {
  if (!lastSynced) return "Never synced";

  const syncDate = new Date(lastSynced);
  const now = new Date();
  const diffInMinutes = Math.floor(
    (now.getTime() - syncDate.getTime()) / (1000 * 60)
  );

  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;

  // For older dates, show the actual date
  return syncDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: syncDate.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
};

// Function to get card styles based on card type or account type
const getCardStyles = (
  cardType = "default",
  accountType = "",
  accountSubtype = ""
) => {
  // Define card style presets based on the image design
  const styles: StylesType = {
    // Credit card style - green gradient
    credit: {
      background:
        "bg-gradient-to-r from-[var(--card-bg-1)] to-[var(--card-bg-secondary-1)]",
      textPrimary: "text-white",
      cardTitle: "Credit Card",
      accentColor: "bg-emerald-500",
    },
    // Debit card style - red gradient
    debit: {
      background:
        "bg-gradient-to-r from-[var(--card-bg-2)] to-[var(--card-bg-secondary-2)]",
      textPrimary: "text-white",
      cardTitle: "Debit Card",
      accentColor: "bg-rose-500",
    },
    // Bank transfer style - blue gradient
    transfer: {
      background:
        "bg-gradient-to-r from-[var(--card-bg-3)] to-[var(--card-bg-secondary-3)]",
      textPrimary: "text-white",
      cardTitle: "Bank Transfer",
      accentColor: "bg-blue-500",
    },
    // Default style based on account type
    default: {
      background:
        "bg-gradient-to-r from-[var(--card-bg-3)] to-[var(--card-bg-secondary-3)]",
      textPrimary: "text-white",
      cardTitle: "Bank Card",
      accentColor: "bg-blue-500",
    },
  };

  // Return style based on explicit card type
  if (cardType && cardType !== "default") {
    return styles[cardType as keyof StylesType];
  }

  // Or determine from account type as fallback
  if (accountType === "credit") {
    return styles.credit;
  } else if (accountType === "depository") {
    if (accountSubtype === "checking") {
      return styles.debit;
    } else if (accountSubtype === "savings") {
      return styles.transfer;
    }
  }

  return styles.default;
};

export const PaymentCard: React.FC<PaymentCardProps> = ({
  account,
  cardType,
  isSelected,
  onSelect,
}) => {
  const { handleSyncTransaction, loadingStates, clickedSyncID } = useContext(
    LinkedAccountsContext
  );
  const cardStyle = getCardStyles(cardType, account.type, account.subtype);
  // Format card number - last 4 digits from mask, rest are X's
  const cardNumber = `XXXX XXXX XXXX ${account.mask || "0000"}`;

  // Format balance amount
  const balanceAmount = formatUniversalNumber(account.currentBalance);

  const syncTransaction = async () => {
    await handleSyncTransaction(account.plaidAccountId);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header section - fixed height */}
      <div className="flex justify-between flex-wrap mb-1 min-h-[2rem] items-center">
        <span className="BookEZ_font_bold BookEZ_font_body_size_large BookEZ_font_color_gray_2 truncate flex-1 mr-2">
          {cardStyle.cardTitle}
        </span>
        <div className="flex gap-1 items-center flex-shrink-0">
          {!account.isUploadAccount && (
            <Button variant={"default"} size={"icon"} onClick={syncTransaction}>
              {loadingStates?.isSyncingTransactions &&
              clickedSyncID === account.plaidAccountId ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          )}
          <p className="BookEZ_font_body_small BookEZ_font_color_gray_1 BookEZ_font_light_bold truncate max-w-[100px]">
            {getLastSyncedTime(account.lastTransactionSyncedDate)}
          </p>
        </div>
      </div>

      {/* Card section - flexible height */}
      <div
        onClick={() => onSelect?.(account.plaidAccountId)}
        className={`relative w-full rounded-2xl overflow-hidden transition-all duration-500 cursor-pointer group flex-1 flex flex-col
        ${cardStyle.background} px-4 py-3 h-[122px]
        ${
          isSelected
            ? "ring-2 ring-blue-400/70 transform scale-[1.03] brightness-110"
            : "shadow-lg hover:shadow-xl hover:scale-[1.01] hover:brightness-105"
        }
      `}
      >
        {/* Enhanced selection indicator with much more visible background */}
        {isSelected && (
          <>
            {/* Strong visible selection overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/40 via-blue-400/30 to-purple-500/40 rounded-2xl"></div>

            {/* Additional bright border overlay */}
            <div className="absolute inset-0 rounded-2xl border-4 border-blue-400/80 shadow-[0_0_30px_rgba(59,130,246,0.5)]"></div>

            {/* Corner badge */}
            <div className="absolute top-2 right-2 z-[3]">
              <div className="bg-blue-500 backdrop-blur-sm rounded-full p-1.5 shadow-lg border-2 border-white">
                <Check className="h-4 w-4 text-white" />
              </div>
            </div>

            {/* Animated glow effect */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-400/20 via-blue-300/20 to-blue-400/20 animate-pulse"></div>
          </>
        )}

        {/* Lines pattern background overlay */}
        <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
          <Lines className="w-full h-full object-cover opacity-60" />
        </div>

        {/* Header with balance - fixed height */}
        <div className="flex justify-between items-center relative z-10 mb-2 mt-2 px-2">
          {!account.isUploadAccount && (
            <div className="text-white flex gap-1 items-center BookEZ_font_body_medium">
              <span className="text-white/90 text-sm">Bal:</span>{" "}
              <span className="font-semibold text-sm sm:text-base truncate max-w-[120px] inline-block">
                {balanceAmount}
              </span>
            </div>
          )}
        </div>

        {/* Account name - fixed height with truncation */}
        <div className="mt-4 mb-2 relative z-10 px-2 flex items-center">
          <div
            className="text-white BookEZ_font_body_medium text-base sm:text-lg font-medium truncate w-full"
            title={account.name}
          >
            {account.name}
          </div>
        </div>

        {/* Card number - fixed height */}
        <div className="mb-4 relative z-10 px-2 min-h-[2rem] flex items-center">
          <div className="text-white text-lg sm:text-xl tracking-wider font-mono font-light truncate w-full">
            {cardNumber}
          </div>
        </div>

        {/* Bank name and status indicator - fixed at bottom */}
        <div className="relative z-10 mt-auto flex justify-between items-center px-2 pb-2 min-h-[1.5rem]">
          <div
            className="text-white text-xs sm:text-sm opacity-90 truncate flex-1"
            title={account.officialName}
          >
            {account.officialName}
          </div>
        </div>

        {/* Enhanced selection animation overlay */}
        {isSelected && (
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent 
                          translate-x-[-100%] animate-[shimmer_3s_ease-in-out_infinite]"
            ></div>
          </div>
        )}
      </div>
    </div>
  );
};

// Responsive Grid Container Component
interface PaymentCardGridProps {
  cards: Array<{
    account: UserAccount & { lastSynced?: string | Date };
    cardType?: "credit" | "debit" | "transfer";
    isSelected?: boolean;
    onSelect?: (accountId: string) => void;
    onSync?: (accountId: string) => Promise<void>;
  }>;
}

export const PaymentCardGrid: React.FC<PaymentCardGridProps> = ({ cards }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
      {cards.map((cardProps, index) => (
        <div key={cardProps.account.plaidAccountId || index} className="h-full">
          <PaymentCard {...cardProps} />
        </div>
      ))}
    </div>
  );
};

export default PaymentCard;
